<template>
  <v-row no-gutters class="justify-center algin-center">
    <v-col cols="auto">
      <table-button key="toggleEditing" v-bind="mainButton" @toggleEditing="toggleEditing"> </table-button>
    </v-col>
    <v-col cols="auto" v-if="isEditingOn">
      <table-buttons
        :buttons="filteredButtons"
        @addNewObject="toggleAddNewObject"
        @copyObjectsGeometry="toggleCopyObjectsGeometry"
        @copyObject="toggleCopyObject"
        @deleteObject="deleteObjectInit"
        @bulkEdit="openFieldCalculator"
        @attributesPainter="toggleAttributesPainter"
        @invertLine="invertLineInit"
        @splitPolygon="toggleSplitingPolygon"
        @joinPolygons="joinPolygons"
        @dataCalculator="openDataCalculator"
        @assignParcel="toggleAssignParcel"
        @preciseDigitizing="togglePreciseDigitizing"
      >
        <template #joinLines-outter="{ icon, fontSize, disabled, tooltip }">
          <table-buttons-edit-join-lines
            :selected-feature-id="selectedFeatureId"
            @joinLines="joinLineInit"
            :tooltip="tooltip"
            :icon="icon"
            :datasource="datasource"
            :font-size="fontSize"
            :disabled="disabled"
            :layer-id="layerId"
            :topological="isTopo"
          ></table-buttons-edit-join-lines>
        </template>
        <template #splitLine-outter="{ icon, fontSize, disabled, tooltip, isActive, isLoading }">
          <table-buttons-edit-split-line
            @splitLine="toggleSplitingLine"
            @splitLineHalf="toggleSplitingLineHalf"
            :tooltip="tooltip"
            :icon="icon"
            :font-size="fontSize"
            :disabled="disabled"
            :is-active="isActive"
            :is-loading="isLoading"
          ></table-buttons-edit-split-line>
        </template>
        <template #moveRotateGeometry-outter="{ icon, fontSize, disabled, tooltip, isActive, isLoading }">
          <table-buttons-edit-move-rotate
            @toggle="toggleMoveRotateObject"
            @accept="moveRotateGeometryHandler"
            :tooltip="tooltip"
            :icon="icon"
            :font-size="fontSize"
            :disabled="disabled"
            :is-active="isActive"
            :is-loading="isLoading"
          ></table-buttons-edit-move-rotate>
        </template>
        <template #changeGeometry-outter="item">
          <table-buttons-edit-change-geometry
            @toggle="toggleEditObject"
            :geometry-type="geometryType"
            v-bind="item"
          ></table-buttons-edit-change-geometry>
        </template>
        <template #assignMultipleParcel-outter="{ icon, fontSize, disabled, tooltip, isActive, isLoading }">
          <table-buttons-assign-multiple-parcels
            @toggle="toggleAssignParcel('multiple')"
            @accept="moveRotateGeometryHandler"
            :geometry-type="geometryType"
            :tooltip="tooltip"
            :icon="icon"
            :font-size="fontSize"
            :disabled="disabled"
            :is-active="isActive"
            :is-loading="isLoading"
          ></table-buttons-assign-multiple-parcels>
        </template>
      </table-buttons>
    </v-col>
    <main-dialog
      :is-visible.sync="isDialogVisible"
      v-if="isDialogLoaded"
      :loading="agreeButtonLoading"
      v-bind="dialogConfig"
      @agree="dialogConfig.agreeHandler"
      @modalClosed="onModalClosed"
    ></main-dialog>
    <table-buttons-copy-objects-geometry-dialog
      v-if="isCopyObjectsGeometryLoaded"
      :is-visible.sync="isCopyObjectsGeometryVisible"
      :is-loading.sync="isCopyObjectGeoemtryLoading"
      :input-layer-id="currentLayerId"
      :input-features-ids="selectedFeatures"
      @topoCopyErrors="onTopoObjectsCopyErrors"
    />
    <table-buttons-copy-objects-geometry-errors-dialog
      v-if="isCopyObjectsGeometryErrorsLoaded"
      :is-visible.sync="isCopyObjectsGeometryErrorsVisible"
      :errors="copyObjectsGeometryErrors"
    />
    <data-calculator-dialog v-if="isDataCalculatorLoaded" :is-visible.sync="isDataCalculatorVisible" />
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import { prepareDrawnGeometry, createVectorSource } from '@/assets/js/mapUtils';
import TableButton from '@/components/TableButton';
import TableButtons from '@/components/TableButtons';
import TableButtonsEditJoinLines from '@/components/TableButtonsEditJoinLines';
import TableButtonsEditSplitLine from '@/components/TableButtonsEditSplitLine';
import TableButtonsEditMoveRotate from '@/components/TableButtonsEditMoveRotate';
import TableButtonsEditChangeGeometry from '@/components/TableButtonsEditChangeGeometry';
import TableButtonsAssignMultipleParcels from '@/components/TableButtonsAssignMultipleParcels';

import deleteObject from '@/mixins/deleteObject';
import geometryTypeMapping from '@/mixins/map/geometryTypeMapping';
import * as tableEditButtonsMixins from '@/mixins/tableButtonsEdit';
import topo from '@/mixins/topo';

export default {
  name: 'TableButtonsEdit',
  mixins: [deleteObject, geometryTypeMapping, ...Object.values(tableEditButtonsMixins), topo],
  components: {
    MainDialog: () => import('@/components/MainDialog'),
    DataCalculatorDialog: () => import('@/components/DataCalculatorDialog'),
    TableButtonsCopyObjectsGeometryDialog: () => import('@/components/TableButtonsCopyObjectsGeometryDialog'),
    TableButtonsCopyObjectsGeometryErrorsDialog: () =>
      import('@/components/TableButtonsCopyObjectsGeometryErrorsDialog'),
    TableButton,
    TableButtons,
    TableButtonsEditJoinLines,
    TableButtonsEditSplitLine,
    TableButtonsEditMoveRotate,
    TableButtonsEditChangeGeometry,
    TableButtonsAssignMultipleParcels,
  },
  props: {
    selectedFeatures: {
      type: Array,
      default: () => [],
    },
    layerId: {
      type: [Number, String],
      required: true,
    },
    isAnyFeatureSelected: {
      type: Boolean,
      default: false,
    },
    datasource: {
      type: String,
      required: true,
    },
    geometryType: {
      validator: value => {
        return typeof value === 'string' || value === null;
      },
      required: true,
    },
    layerScope: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isEditingOn: false,
    isDialogVisible: false,
    isDialogLoaded: false,
    isCopyButtonLoading: false,
    isSplittingLineButtonLoading: false,
    dialogType: 'delete',
    joinWithId: undefined,
    agreeButtonLoading: false,
    treeViewMainLayerId: null,
    isCopyObjectsGeometryLoaded: false,
    isCopyObjectsGeometryVisible: false,
    isCopyObjectGeoemtryLoading: false,
    isCopyObjectsGeometryErrorsLoaded: false,
    isCopyObjectsGeometryErrorsVisible: false,
    copyObjectsGeometryErrors: [],
  }),
  computed: {
    additionalAttributesValues: sync('layers/additionalAttributesValues'),
    currentLayer: get('layers/currentLayer'),
    currentLayerId: get('layers/currentLayer@id'),
    tableSelectionType: get('edit/tableSelectionType'),
    currentTab: sync('sidebar/currentTab'),
    newFeatureGeometry: sync('edit/newFeatureGeometry'),
    isAddingButtonActive: sync('edit/isAddingButtonActive'),
    isEditingButtonActive: sync('edit/isEditingButtonActive'),
    isMoveRotateButtonActive: sync('edit/isMoveRotateButtonActive'),
    isCopyButtonActive: sync('edit/isCopyButtonActive'),
    isSplittingLineButtonActive: sync('edit/isSplittingLineButtonActive'),
    isAssignParcelButtonActive: sync('edit/isAssignParcelButtonActive'),
    isAssignMultipleParcelButtonActive: sync('edit/isAssignMultipleParcelButtonActive'),
    isAssigningMultipleParcelsLoading: get('edit/isAssigningMultipleParcelsLoading'),
    isAttributesPainterButtonActive: sync('edit/isAttributesPainterButtonActive'),
    attributesPainterObject: sync('edit/attributesPainterObject'),
    attributesPainterObjects: sync('edit/attributesPainterObjects'),
    copiedObject: sync('edit/copiedObject'),
    activeTool: get('tools/activeTool'),
    treeViewMetadata: get('attributesSchema/treeViewMetadata@:currentLayerId'),
    isActiveToolNotReplaceable: get('tools/isActiveToolNotReplaceable'),
    zdmStreetLampDataSourceName: get('admin/modulesMapping@zdm_data.datasources.street_lamp.datasource'),
    zdmStreetLampFields: get('admin/modulesMapping@zdm_data.street_lamp_fields'),
    attachmentsDataSourceName: get('admin/modulesMapping@documentation.attachment_datasource_name'),
    geometryPropertyName: get('layers/metadata@:datasource.attributes_schema.geometry_name'),
    currentLayerDataSourceMetadata: get('layers/metadata@:datasource'),
    currentLayerDataSourceUseQualifiedNames: get(
      'layers/metadata@:datasource.attributes_use_datasource_qualified_names'
    ),
    currentLayerIdAttributeName: get('layers/metadata@:datasource.attributes_schema.id_name'),
    currentLayerAttributesMetadata: get('layers/metadata@:datasource.attributes_schema.attributes'),
    isAdvancedDigitizingEnabled: get('admin/additionalModules@ADVANCED_DIGITIZING.enabled'),
    advancedDigitizingPermissions: get('users/toolsPermissions@ADVANCED_DIGITIZING.main_value'),
    splitMergePolygonsEnabled() {
      return (
        this.isAdvancedDigitizingEnabled && this.isSplitMergePolygonsEnabled && this.advancedDigitizingPermissions > 0
      );
    },
    isSplitMergePolygonsEnabled: get('admin/settingsValues@split_merge_polygons_enabled'),
    projectElements: get('layers/project@layers'),
    isAdminParcelsEnabled: get('admin/settingsValues@parcels_module_enabled'),
    parcelsModuleMetadata: get('admin/additionalModules@PARCELS'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    geometryReadOnly() {
      return (
        this.currentLayerAttributesMetadata?.find(attribute => attribute.name === this.geometryPropertyName)
          ?.read_only || false
      );
    },
    fid() {
      return this.$route.params.fid;
    },
    selectedFeatureId() {
      const {
        selectedFeatures: [id],
      } = this;
      return id;
    },
    selectedFeaturesLength() {
      return this.selectedFeatures.length;
    },
    isOneFeatureSelected() {
      return this.selectedFeaturesLength === 1;
    },
    isAtLeastOneObjectSelected() {
      return this.selectedFeaturesLength >= 1;
    },
    isMoreThanOneObjectSelected() {
      return this.selectedFeaturesLength > 1;
    },
    mainButton() {
      return {
        name: 'toggleEditing',
        icon: 'mdi-pencil',
        fontSize: '19px',
        isActive: this.isEditingOn,
        hasState: true,
        tooltip: this.isEditingOn
          ? this.$i18n.t('button.toggleEditing', { onOff: this.$i18n.t('button.off') })
          : this.$i18n.t('button.toggleEditing', { onOff: this.$i18n.t('button.on') }),
        hasLeftDivider: true,
      };
    },
    isGeometricalLayer() {
      return Boolean(this.currentLayer.geometry_type);
    },
    isTopo() {
      return this.isTopologicalLayer({ id: this.layerId, layer_scope: this.layerScope });
    },
    isLineStringGeometry() {
      return this.geometryType === 'linestring' || this.geometryType === 'multilinestring';
    },
    isPolygonGeometry() {
      return this.geometryType === 'polygon' || this.geometryType === 'multipolygon';
    },
    olGeometryType() {
      return this.getOlGeometryTypeFromLayerGeometry(this.geometryType);
    },
    isAssignParcelVisible() {
      return (
        this.isPolygonGeometry &&
        this.parcelsModuleMetadata?.enabled &&
        this.parcelsModuleMetadata?.configured &&
        this.isAdminParcelsEnabled
      );
    },
    streetLampFieldsDataSources() {
      let dataSources = [];
      if (this.zdmStreetLampFields) {
        dataSources = Object.keys(this.zdmStreetLampFields);
      }
      if (this.zdmStreetLampDataSourceName) {
        dataSources.push(this.zdmStreetLampDataSourceName);
      }
      return dataSources;
    },
    isAttachmentsDatasource() {
      return this.datasource === this.attachmentsDataSourceName;
    },
    buttons() {
      return [
        ...(!this.isAttachmentsDatasource && !this.geometryReadOnly
          ? [
              {
                name: 'addNewObject',
                icon: 'mdi-plus-circle',
                disabled: this.isButtonDisabled({ skipButtons: ['isAddingButtonActive'] }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isAddingButtonActive'] }),
                  enableTooltip: 'button.addNewObject',
                }),
                fontSize: '19px',
                hasState: true,
                isActive: this.isAddingButtonActive,
              },
              {
                name: 'preciseDigitizing',
                icon: 'mdi-math-compass',
                disabled: this.isButtonDisabled({ skipButtons: ['isPreciseDigitizingButtonActive'] }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isPreciseDigitizingButtonActive'] }),
                  enableTooltip: 'button.preciseDigitizing',
                }),
                fontSize: '19px',
                hasState: true,
                isActive: this.isPreciseDigitizingButtonActive,
                isNotVisible:
                  !this.preciseDigitizingEnabled ||
                  !this.isGeometricalLayer ||
                  !(this.isLineStringGeometry || this.isPolygonGeometry),
              },
            ]
          : []),
        ...(this.isGeometricalLayer && !this.geometryReadOnly
          ? [
              {
                name: 'changeGeometry',
                icon: 'mdi-map-marker',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isEditingButtonActive'],
                  additionalCondition: !this.isOneFeatureSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({
                    skipButtons: ['isEditingButtonActive'],
                  }),
                  condition: this.isOneFeatureSelected,
                  enableTooltip: 'button.changeGeometry',
                  disableTooltip: 'button.toChangeGeometry',
                  requirement: 'button.justOneObject',
                }),
                fontSize: '19px',
                hasState: true,
                isActive: this.isEditingButtonActive,
              },
            ]
          : []),
        {
          name: 'bulkEdit',
          icon: 'mdi-table-large',
          disabled: this.isButtonDisabled({ additionalCondition: !this.isMoreThanOneObjectSelected }),
          tooltip: this.getButtonTooltip({
            isDisabledByAnotherTool: this.isButtonDisabled(),
            condition: this.isMoreThanOneObjectSelected,
            enableTooltip: 'button.bulkAttributesEdit',
            disableTooltip: 'button.toBulkEdit',
            requirement: 'button.moreThanOneObject',
          }),
          fontSize: '19px',
        },
        {
          name: 'attributesPainter',
          icon: '$paintbrush',
          disabled: this.isButtonDisabled({
            skipButtons: ['isAttributesPainterButtonActive'],
            additionalCondition: !this.isMoreThanOneObjectSelected,
          }),
          tooltip: this.getButtonTooltip({
            isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isAttributesPainterButtonActive'] }),
            condition: this.isAtLeastOneObjectSelected,
            enableTooltip: 'button.attributesPainter',
            disableTooltip: 'button.toAttributesPainter',
            requirement: 'button.atLeastOneSelected',
          }),
          fontSize: '19px',
          hasState: true,
          isActive: this.isAttributesPainterButtonActive,
        },
        ...this.dataCalculatorButton,
        ...(this.isGeometricalLayer && !this.geometryReadOnly
          ? [
              {
                name: 'copyObjectsGeometry',
                icon: 'mdi-content-copy',
                disabled: !this.isAtLeastOneObjectSelected,
                tooltip: this.getButtonTooltip({
                  condition: this.isAtLeastOneObjectSelected,
                  enableTooltip: 'sidebar.copyObjectsGeometry.title',
                  disableTooltip: 'button.toCopyObjectGeoemtry',
                  requirement: 'button.atLeastOneSelected',
                }),
                fontSize: '19px',
              },
            ]
          : []),
        ...(!this.isAttachmentsDatasource && !this.geometryReadOnly
          ? [
              {
                name: 'copyObject',
                icon: '$tableLargeCopy',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isCopyButtonActive'],
                  additionalCondition: !this.isOneFeatureSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isCopyButtonActive'] }),
                  condition: this.isOneFeatureSelected,
                  enableTooltip: 'button.copyObject',
                  disableTooltip: 'button.toCopyObject',
                  requirement: 'button.justOneObject',
                }),
                fontSize: '19px',
                hasState: true,
                isActive: this.isCopyButtonActive,
                isLoading: this.isCopyButtonLoading,
              },
            ]
          : []),
        ...(this.isGeometricalLayer && !this.geometryReadOnly
          ? [
              {
                name: 'invertLine',
                icon: 'mdi-swap-horizontal',
                disabled: this.isButtonDisabled({
                  additionalCondition: !this.isOneFeatureSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled(),
                  condition: this.isOneFeatureSelected,
                  enableTooltip: 'button.invertLine',
                  disableTooltip: 'button.toInvertLine',
                  requirement: 'button.justOneObject',
                }),
                fontSize: '19px',
                isNotVisible: !(this.isTopo && this.isLineStringGeometry),
              },
              {
                name: 'splitLine',
                icon: 'mdi-box-cutter',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isSplittingLineButtonActive'],
                  additionalCondition: !this.isOneFeatureSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isSplittingLineButtonActive'] }),
                  condition: this.isOneFeatureSelected,
                  enableTooltip: 'button.splitLine',
                  disableTooltip: 'button.toSplitLine',
                  requirement: 'button.justOneObject',
                }),
                fontSize: '19px',
                hasState: true,
                isActive: this.isSplittingLineButtonActive,
                isLoading: this.isSplittingLineButtonLoading,
                isNotVisible: !this.isLineStringGeometry,
              },
              {
                name: 'splitPolygon',
                icon: 'mdi-box-cutter',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isSplittingPolygonButtonActive'],
                  additionalCondition: !this.isOneFeatureSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isSplittingPolygonButtonActive'] }),
                  condition: this.isOneFeatureSelected,
                  enableTooltip: 'button.splitPolygon',
                  disableTooltip: 'button.toSplitPolygon',
                  requirement: 'button.justOneObject',
                }),
                fontSize: '19px',
                hasState: true,
                isActive: this.isSplittingPolygonButtonActive,
                isLoading: this.isSplittingPolygonButtonLoading,
                isNotVisible: !this.isPolygonGeometry || !this.splitMergePolygonsEnabled,
              },
              {
                name: 'joinLines',
                icon: 'mdi-vector-polyline',
                disabled: this.isButtonDisabled({
                  additionalCondition: !this.isOneFeatureSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled(),
                  condition: this.isOneFeatureSelected,
                  enableTooltip: 'button.joinLines',
                  disableTooltip: 'button.toJoinLines',
                  requirement: 'button.justOneObject',
                }),
                fontSize: '19px',
                isNotVisible: !this.isLineStringGeometry,
              },
              {
                name: 'joinPolygons',
                icon: 'mdi-vector-union',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isAddingButtonActive'],
                  additionalCondition: !this.isMoreThanOneObjectSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isAddingButtonActive'] }),
                  condition: this.isMoreThanOneObjectSelected,
                  enableTooltip: 'button.joinPolygons',
                  disableTooltip: 'button.toJoinPolygons',
                  requirement: 'button.moreThanOneObject',
                }),
                fontSize: '19px',
                isLoading: this.isJoiningPolygonsButtonLoading,
                isNotVisible: !this.isPolygonGeometry || !this.splitMergePolygonsEnabled,
              },
              {
                name: 'moveRotateGeometry',
                icon: '$cropSquare',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isMoveRotateButtonActive'],
                  additionalCondition: !this.isOneFeatureSelected,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isMoveRotateButtonActive'] }),
                  condition: this.isOneFeatureSelected,
                  enableTooltip: 'button.moveRotateGeometry',
                  disableTooltip: 'button.toMoveRotateGeometry',
                  requirement: 'button.justOneObject',
                }),
                fontSize: '19px',
                isLoading: this.isMoveRotateButtonLoading,
                isNotVisible:
                  (!this.isLineStringGeometry && !this.isPolygonGeometry) || !this.moveRotateGeometryEnabled,
                isActive: this.isMoveRotateButtonActive,
              },
              {
                name: 'assignParcel',
                icon: 'mdi-crosshairs-gps',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isAssignParcelButtonActive'],
                  additionalCondition:
                    this.activeTool !== 'moduleIdentification' &&
                    this.activeTool !== 'identification-assignPlot' &&
                    this.isActiveToolNotReplaceable,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({
                    skipButtons: ['isAssignParcelButtonActive'],
                    additionalCondition:
                      this.activeTool !== 'moduleIdentification' &&
                      this.activeTool !== 'identification-assignPlot' &&
                      this.isActiveToolNotReplaceable,
                  }),
                  enableTooltip: 'button.assignParcelGeometry',
                  disableTooltip: 'button.toChangeGeometry',
                  requirement: 'button.justOneObject',
                }),
                loading: !this.uldkAttributesFetched,
                fontSize: '19px',
                hasState: true,
                isActive: this.isAssignParcelButtonActive,
                isNotVisible: !this.isAssignParcelVisible,
              },
              {
                name: 'assignMultipleParcel',
                icon: '$multiparcel',
                disabled: this.isButtonDisabled({
                  skipButtons: ['isAssignMultipleParcelButtonActive'],
                  additionalCondition:
                    this.activeTool !== 'moduleIdentification' &&
                    this.activeTool !== 'identification-assignMultiplePlots' &&
                    this.isActiveToolNotReplaceable,
                }),
                tooltip: this.getButtonTooltip({
                  isDisabledByAnotherTool: this.isButtonDisabled({
                    skipButtons: ['isAssignMultipleParcelButtonActive'],
                    additionalCondition:
                      this.activeTool !== 'moduleIdentification' &&
                      this.activeTool !== 'identification-assignMultiplePlots' &&
                      this.isActiveToolNotReplaceable,
                  }),
                  enableTooltip: 'button.assignMultipleParcelsGeometry',
                  disableTooltip: 'button.toChangeGeometry',
                  requirement: 'button.justOneObject',
                }),
                isLoading: this.isAssigningMultipleParcelsLoading,
                fontSize: '23px',
                hasState: true,
                isActive: this.isAssignMultipleParcelButtonActive,
                isNotVisible: !this.isAssignParcelVisible,
              },
            ]
          : []),
        {
          name: 'deleteObject',
          icon: 'mdi-delete',
          disabled: this.isButtonDisabled({
            skipButtons: ['isAddingButtonActive'],
            additionalCondition: !this.isAtLeastOneObjectSelected,
          }),
          tooltip: this.getButtonTooltip({
            isDisabledByAnotherTool: this.isButtonDisabled({ skipButtons: ['isAddingButtonActive'] }),
            condition: this.isAtLeastOneObjectSelected,
            enableTooltip: this.isOneFeatureSelected ? 'button.deleteObject' : 'button.deleteObjects',
            disableTooltip: 'button.noObjectsSelected',
            requirement: 'button.toDeleteObject',
          }),
          fontSize: '19px',
        },
      ];
    },
    filteredButtons() {
      return this.buttons.filter(button => !button.isNotVisible);
    },
    dialogConfig() {
      const dict = {
        delete: {
          title: this.isOneFeatureSelected
            ? this.$i18n.t('dialog.title.deleteObject')
            : this.$i18n.t('dialog.title.deleteObjects'),
          body: this.isOneFeatureSelected
            ? this.$i18n.t('dialog.body.deleteObject', { id: this.selectedFeatureId })
            : this.$i18n.t('dialog.body.deleteObjects'),
          agreeText: 'dialog.agreeDelete',
          agreeHandler: this.deleteObjectHandler,
        },
        invert: {
          title: this.$i18n.t('dialog.title.invertLine'),
          body: this.$i18n.t('dialog.body.invertLine', { id: this.selectedFeatureId }),
          agreeText: 'dialog.invert',
          agreeHandler: this.invertLineHandler,
        },
        split: {
          title: this.$i18n.t('dialog.title.splitLine'),
          body: this.$i18n.t('dialog.body.splitLine', { id: this.selectedFeatureId }),
          agreeText: 'dialog.split',
          agreeHandler: this.splitLineHandler,
        },
        splitPolygon: {
          title: this.$i18n.t('dialog.title.splitPolygon'),
          body: this.$i18n.t('dialog.body.splitPolygon', { id: this.selectedFeatureId }),
          agreeText: 'dialog.split',
          agreeHandler: this.splitPolygonHandler,
        },
        joinPolygons: {
          title: this.$i18n.t('dialog.title.joinPolygons'),
          body: this.$i18n.t('dialog.body.joinPolygons', { id: this.selectedFeatures }),
          agreeText: 'dialog.join',
          agreeHandler: this.joinPolygonsHandler,
        },
        join: {
          title: this.$i18n.t('dialog.title.joinLine'),
          body: this.$i18n.t('dialog.body.joinLine', { id: this.selectedFeatureId, joinWithId: this.joinWithId }),
          agreeText: 'dialog.join',
          agreeHandler: this.joinLinesHandler,
        },
      };
      return dict[this.dialogType];
    },
  },
  methods: {
    getTreeMetadata: call('attributesSchema/getTreeMetadata'),
    getLayerFeature: call('layers/getLayerFeature'),
    invertLine: call('layers/invertLine'),
    joinTopologicalLines: call('layers/joinTopologicalLines'),
    joinLines: call('layers/joinLines'),
    splitTopologicalLine: call('layers/splitTopologicalLine'),
    splitLine: call('layers/splitLine'),
    tableLayerChanged(oV, nV) {
      if ((nV?.id || nV?.id === 0) && nV?.id == this.treeViewMainLayerId) {
        return;
      }
      this.turnOffEditing();
    },
    toggleEditing() {
      if (this.isEditingOn) {
        this.turnOffEditing();
        return;
      }
      this.isEditingOn = !this.isEditingOn;
    },
    turnOffEditing() {
      if (this.isAddingButtonActive) {
        this.turnOffAddNewObject();
      }
      if (this.isEditingButtonActive) {
        this.turnOffEditObject();
      }
      if (this.isCopyButtonActive) {
        this.turnOffCopying();
      }
      if (this.isMoveRotateButtonActive) {
        this.turnOffMoveRotateObject();
      }
      this.isEditingOn = false;
    },
    pushToParentRoute() {
      if (!this.$route.meta.parent === this.$route.name) {
        return;
      }
      this.$router.push({
        name: this.$route.meta.parent,
        params: {
          lid: this.layerId,
        },
      });
      this.currentTab = 0;
    },
    setSidebarGeometry(feature) {
      if (!feature) {
        this.newFeatureGeometry = null;
      } else {
        this.newFeatureGeometry = prepareDrawnGeometry(feature);
      }
    },
    onModifyend(e) {
      this.setSidebarGeometry(e.features.getArray()[0]);
    },
    onDrawend(e) {
      this.setSidebarGeometry(e.feature);
      if (this.streetLampFieldsDataSources.includes(this.datasource)) {
        if (this.$route.name === 'addingLamp') {
          return;
        }
        this.$router.push({
          name: 'addingLamp',
          params: {
            ...this.$route.params,
          },
        });
        return;
      }
      const layerId =
        this.treeViewMainLayerId || this.treeViewMainLayerId === 0 ? this.treeViewMainLayerId : this.layerId;
      this.$router.push({
        name: 'layerFeature',
        params: {
          ...this.$route.params,
          lid: layerId,
          fid: 'new',
        },
      });
    },
    openDialog(type) {
      this.dialogType = type;
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    invertLineInit() {
      this.openDialog('invert');
    },
    joinLineInit(id) {
      this.joinWithId = id;
      this.openDialog('join');
    },
    deleteObjectInit() {
      this.openDialog('delete');
    },
    onModalClosed() {
      if (this.dialogType === 'split') {
        this.turnOffSplittingLine();
      } else if (this.dialogType === 'splitPolygon') {
        this.turnOffSplittingPolygon();
      }
    },
    clearModal() {
      this.agreeButtonLoading = false;
      this.isDialogVisible = false;
    },
    async agreeHof(fn, payload, message, successFunction) {
      try {
        this.agreeButtonLoading = true;
        const r = await fn(payload);
        this.pushMessage(message);
        this.clearModal();
        if (successFunction) {
          successFunction(payload);
        } else {
          this.refreshLayersAndTable(r.data.data);
        }
        return r;
      } catch (error) {
        this.clearModal();
        throw new Error(error);
      }
    },
    async joinLinesHandler() {
      const { selectedFeatureId: id, joinWithId, datasource } = this;
      await this.agreeHof(
        this.isTopo ? this.joinTopologicalLines : this.joinLines,
        this.isTopo
          ? { body: { features: [id, joinWithId] }, datasource, params: { layer_id: this.layerId } }
          : { body: { to_merge: [joinWithId] }, datasource, id, params: { layer_id: this.layerId } },
        this.$i18n.t('success.joinedLines'),
        !this.isTopo ? this.refreshLayersAndTableByDataSource : null
      );
    },
    async invertLineHandler() {
      const { selectedFeatureId: id, datasource } = this;
      await this.agreeHof(
        this.invertLine,
        { id, datasource, params: { layer_id: this.layerId } },
        this.$i18n.t('success.invertedLine')
      );
    },
    async splitLineHandler() {
      const { selectedFeatureId: id, datasource, splitGeometry } = this;
      await this.agreeHof(
        this.isTopo ? this.splitTopologicalLine : this.splitLine,
        {
          id,
          datasource,
          body: { split_point: splitGeometry },
          params: { layer_id: this.layerId },
        },
        this.$i18n.t('success.splitedLine'),
        !this.isTopo ? this.refreshLayersAndTableByDataSource : null
      );
      this.turnOffSplittingLine();
    },
    pushMessage(message) {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message,
      });
    },
    onSplitLineDrawEnd(e) {
      this.splitGeometry = prepareDrawnGeometry(e.feature);
      this.openDialog('split');
    },
    toggleSplitingLine() {
      if (this.isSplittingLineButtonActive) {
        this.turnOffSplittingLine();
      } else {
        this.turnOnSplittingLine();
      }
    },
    turnOffSplittingLine() {
      this.$root.$emit('deleteSidebarGeometry');
      this.$root.$emit('turnOffEditing', false);
      this.$root.$emit('toggleSnappingInteraction', { value: false });
      this.$root.$emit('selection-action', this.tableSelectionType);
      this.isSplittingLineButtonActive = false;
    },
    async turnOnSplittingLine() {
      this.$root.$emit('deactivateAllTools');
      await this.$nextTick(async () => {
        this.isSplittingLineButtonLoading = true;
        this.$root.$emit('selection-action');
        const { layerId: layer_id, selectedFeatureId } = this;
        const r = await this.getLayerFeature({ layer_id, feature_id: selectedFeatureId });
        const snappingVectorSource = createVectorSource([r]);
        this.turnOnSplittngDrawing(snappingVectorSource);
        this.turnOnSplittngSnapping(snappingVectorSource);
        this.isSplittingLineButtonLoading = false;
        this.isSplittingLineButtonActive = true;
      });
    },
    turnOnSplittngDrawing(source) {
      this.$root.$emit('drawSidebarGeometry', {
        geometryType: 'Point',
        drawendCallback: this.onSplitLineDrawEnd,
        condition: e => {
          const intersects = source
            .getFeatures()[0]
            .getGeometry()
            .intersectsCoordinate(e.coordinate);
          if (!intersects) {
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: this.$i18n.t('snackbar.pointNotIntersects'),
            });
            return;
          }
          return true;
        },
        forceDefaultSnapping: true,
      });
    },
    turnOnSplittngSnapping(source) {
      this.$root.$emit('toggleSnappingInteraction', {
        value: true,
        source,
      });
    },
    async deleteObjectHof(fn) {
      try {
        const { datasource, selectedFeatureId, selectedFeatures } = this;
        this.agreeButtonLoading = true;
        const r =
          this.isMoreThanOneObjectSelected || this.isTopo
            ? await fn({
                datasource,
                payload: { to_delete: selectedFeatures },
                params: { layer_id: this.layerId },
              })
            : await fn({ datasource, id: selectedFeatureId, params: { layer_id: this.layerId } });
        this.$emit('refreshTableData');
        this.$root.$emit('refreshMvtSource', this.layerId);
        const layersIds = this.projectLayers
          .filter(layer => layer.data_source_name === datasource)
          .map(layer => layer.id);
        if (layersIds?.length) this.$root.$emit('updateLegendStylesCounts', { layersIds });
        if (selectedFeatureId == this.fid) {
          this.pushToParentRoute();
        }
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.isOneFeatureSelected
            ? this.$i18n.t('success.successDeleteObject')
            : this.$i18n.t('success.successDeleteObjects'),
        });
        return r;
      } catch (error) {
        console.log(error);
      } finally {
        this.clearModal();
      }
    },
    toggleAddNewObject() {
      if (this.isAddingButtonActive) {
        this.turnOffAddNewObject();
      } else {
        this.turnOnAddNewObject();
      }
    },
    turnOnAddNewObject() {
      this.$root.$emit('deactivateAllTools');
      this.$nextTick(() => {
        this.isAddingButtonActive = true;
        if (this.isGeometricalLayer) {
          this.$root.$emit('drawSidebarGeometry', {
            geometryType: this.geometryType,
            drawendCallback: this.onDrawend,
            layerId: this.layerId,
          });
        } else {
          this.onNewPolygonFeatureGeometry({ inputName: 'newPolygonFeatureGeometry', geometry: undefined });
        }
      });
    },
    turnOffAddNewObject() {
      this.isAddingButtonActive = false;
      this.$root.$emit('deleteSidebarGeometry');
      this.pushToParentRoute();
      this.$root.$emit('turnOffEditing', false);
    },
    toggleAttributesPainter() {
      if (this.isAttributesPainterButtonActive) {
        this.turnOffAttributesPainter();
      } else {
        this.turnOnAttributesPainter();
      }
    },
    turnOnAttributesPainter() {
      this.$root.$emit('deactivateAllTools');
      this.$nextTick(() => {
        this.isAttributesPainterButtonActive = true;

        this.$root.$off('assignedObjects');
        this.$root.$on('assignedObjects', identifiedFeatures => {
          const currentLayerFeatures =
            identifiedFeatures.find(layerFeatures => layerFeatures.id == this.currentLayerId)?.features || [];
          if (currentLayerFeatures?.length === 0) return;
          else if (currentLayerFeatures?.length === 1) {
            this.attributesPainterObject = currentLayerFeatures[0];
            this.$router.push({ name: 'attributesPainter', params: { lid: this.layerId } });
          } else {
            this.attributesPainterObjects = currentLayerFeatures;
            this.$router.push({ name: 'attributesPainterFeatures', params: { lid: this.layerId } });
          }
          this.isAttributesPainterButtonActive = false;
          this.$root.$emit('identification-action', false);
          this.$root.$off('assignedObjects');
        });

        this.$root.$emit('identification-action', true, { specialType: 'addByMap' });
      });
    },
    turnOffAttributesPainter() {
      this.isAttributesPainterButtonActive = false;
      this.pushToParentRoute();
      this.$root.$emit('identification-action', false);
      this.$root.$off('assignedObjects');
    },
    toggleAssignParcel(type = 'single') {
      if (type === 'single' ? this.isAssignParcelButtonActive : this.isAssignMultipleParcelButtonActive) {
        this.turnOffAssignParcel();
      } else {
        this.turnOnAssignParcel(type);
      }
    },
    turnOnAssignParcel(type = 'single') {
      const isSingle = type === 'single';
      this.$root.$emit('deactivateAllTools');
      this.$nextTick(() => {
        isSingle ? (this.isAssignParcelButtonActive = true) : (this.isAssignMultipleParcelButtonActive = true);
        this.$root.$emit('identification-action', true, {
          specialType: isSingle ? 'assignPlot' : 'assignMultiplePlots',
          geometryType: this.geometryType,
          plotName: 'newPolygonFeatureGeometry',
          isTurnOffOnFail: false,
        });
      });
    },
    turnOffAssignParcel(withToolDisabling = true) {
      this.isAssignParcelButtonActive = false;
      this.isAssignMultipleParcelButtonActive = false;
      this.$root.$emit('deleteSidebarGeometry');
      this.pushToParentRoute();
      if (withToolDisabling) this.$root.$emit('identification-action', false);
      this.$root.$emit('turnOffEditing', false);
    },
    async onNewPolygonFeatureGeometry({ inputName, geometry, attributesValues, area }) {
      if (inputName === 'newPolygonFeatureGeometry') {
        this.newFeatureGeometry = geometry;
        await this.$router.push({
          name: 'layerFeature',
          params: {
            ...this.$route.params,
            lid: this.layerId,
            fid: 'new',
          },
        });
        if (this.uldkAttributesMappings[this.datasource] && attributesValues) {
          const values = {};
          const uldkAttrsMapping = {
            community: 'commune',
            county: 'county',
            district: 'region',
            plot: 'parcel',
            teryt: 'teryt',
            voivodeship: 'voivodeship',
          };
          Object.keys(uldkAttrsMapping).forEach(key => {
            if (this.uldkAttributesMappings[this.datasource][key] && attributesValues[uldkAttrsMapping[key]]) {
              values[this.uldkAttributesMappings[this.datasource][key]] = attributesValues[uldkAttrsMapping[key]];
              if (key === 'teryt') {
                const territValue = attributesValues[uldkAttrsMapping[key]];
                const dotsIndexes = [];
                for (let i = 0; i < territValue.length; i++) {
                  if (territValue[i] === '.') {
                    dotsIndexes.push(i);
                  }
                }
                if (dotsIndexes.length > 2) {
                  values[this.uldkAttributesMappings[this.datasource]['sheet']] = territValue.substring(
                    dotsIndexes[1] + 1,
                    dotsIndexes[2]
                  );
                }
              }
            }
          });
          if (this.uldkAttributesMappings[this.datasource].area) {
            values[this.uldkAttributesMappings[this.datasource].area] = area.toFixed(2);
          }
          this.additionalAttributesValues = values;
        }
      }
    },
    isFeatureCardOpened(id) {
      return this.fid == id;
    },
    async toggleEditObject() {
      if (this.isEditingButtonActive) {
        this.turnOffEditObject();
      } else {
        this.turnOnEditObject();
      }
    },
    async turnOnEditObject() {
      this.$root.$emit('deactivateAllTools');
      await this.$nextTick(async () => {
        const { selectedFeatureId } = this;
        if (!this.isFeatureCardOpened(selectedFeatureId)) {
          await this.$router.push({
            name: 'layerFeature',
            params: {
              ...this.$route.params,
              lid: this.layerId,
              fid: selectedFeatureId,
            },
          });
        } else {
          this.$root.$emit('turnOnEditing');
        }
        this.isEditingButtonActive = true;
      });
    },
    turnOffEditObject() {
      this.isEditingButtonActive = false;
      this.$root.$emit('turnOffEditing', false);
    },
    toggleCopyObject() {
      if (this.isCopyButtonActive) {
        this.turnOffCopying();
      } else {
        this.turnOnCopying();
      }
    },
    async turnOnCopying() {
      this.$root.$emit('deactivateAllTools');
      await this.$nextTick(async () => {
        try {
          this.isCopyButtonActive = true;
          this.isCopyButtonLoading = true;
          const { layerId: layer_id, selectedFeatureId } = this;
          this.$root.$emit('selection-action');
          if (this.isGeometricalLayer) {
            this.$root.$emit('drawSidebarGeometry', {
              geometryType: this.geometryType,
              drawendCallback: this.onDrawend,
            });
          }
          if (this.datasource !== this.zdmStreetLampDataSourceName) {
            const r = await this.getLayerFeature({ layer_id, feature_id: selectedFeatureId });
            const datasourceAttributes = this.currentLayerDataSourceMetadata?.attributes_schema?.attributes;
            const layerProperties = Object.entries(r.properties).reduce((acc, [key, value]) => {
              acc[key] = !datasourceAttributes.find(atr => atr.name === key)?.read_only ? value : null;
              return acc;
            }, {});
            this.copiedObject = layerProperties;
          } else {
            this.copiedObject = selectedFeatureId;
          }
          if (!this.isGeometricalLayer) {
            this.onNewPolygonFeatureGeometry({ inputName: 'newPolygonFeatureGeometry', geometry: undefined });
          }
        } catch (error) {
          console.log('~ error', error);
        }
        this.isCopyButtonLoading = false;
      });
    },
    turnOffCopying() {
      this.isCopyButtonActive = false;
      this.$root.$emit('deleteSidebarGeometry');
      this.pushToParentRoute();
      this.$root.$emit('turnOffEditing', false);
    },
    openFieldCalculator() {
      this.$router.push({ name: 'fieldCalculator', params: { lid: this.layerId } });
    },
    toggleCopyObjectsGeometry() {
      this.isCopyObjectsGeometryLoaded = true;
      this.isCopyObjectsGeometryVisible = true;
    },
    async getTreeLayerMetadata(lId) {
      if (!lId && lId !== 0) {
        return;
      }
      await this.getTreeMetadata({ layerId: lId });
      const cLayerMetadata = this.treeViewMetadata?.find(metadata => metadata.layer === lId);
      this.treeViewMainLayerId = cLayerMetadata.path[0];
    },
    refreshLayersAndTableByDataSource(payload) {
      const { datasource } = payload;
      const layers = this.projectLayers.filter(layer => layer.data_source_name === datasource);
      const layersIds = layers.map(layer => layer.id);
      this.refreshLayersAndTable(layersIds);
    },
    onTopoObjectsCopyErrors(errors) {
      this.copyObjectsGeometryErrors = errors;
      this.isCopyObjectGeoemtryLoading = false;
      this.isCopyObjectsGeometryVisible = false;
      this.isCopyObjectsGeometryErrorsLoaded = true;
      this.isCopyObjectsGeometryErrorsVisible = true;
    },
    isButtonDisabled({ skipButtons = [], canBeUsedWithNotReplaceable = false, additionalCondition = false } = {}) {
      const arr = [
        'isAddingButtonActive',
        'isEditingButtonActive',
        'isMoveRotateButtonActive',
        'isCopyButtonActive',
        'isSplittingLineButtonActive',
        'isSplittingPolygonButtonActive',
        'isAssignParcelButtonActive',
        'isAssignMultipleParcelButtonActive',
        'isAttributesPainterButtonActive',
      ];
      const booleanArr = [
        arr.filter(e => !skipButtons.includes(e)).find(e => this[e]) ? true : false,
        !canBeUsedWithNotReplaceable && this.isActiveToolNotReplaceable,
        additionalCondition,
      ];
      return booleanArr.find(e => !!e) ? true : false;
    },
    getButtonTooltip({
      isDisabledByAnotherTool = false,
      condition = true,
      enableTooltip = '',
      disableTooltip = '',
      requirement = '',
    } = {}) {
      if (isDisabledByAnotherTool) {
        return this.$i18n.t('button.oneToolActive');
      }
      return condition
        ? this.$i18n.t(enableTooltip)
        : this.$i18n.t(disableTooltip, { suffix: this.$i18n.t(requirement) });
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (!oV && nV) {
        if (this.isAddingButtonActive) {
          this.turnOffAddNewObject();
        }
        if (this.isEditingButtonActive) {
          this.turnOffEditObject();
        }
        if (this.isMoveRotateButtonActive) {
          this.turnOffMoveRotateObject();
        }
        if (this.isCopyButtonActive) {
          this.turnOffCopying();
        }
        if (this.isSplittingLineButtonActive) {
          this.turnOffSplittingLine();
        }
        if (this.isSplittingPolygonButtonActive) {
          this.turnOffSplittingPolygon();
        }
        if (this.isAssignParcelButtonActive && nV !== 'identification-assignPlot') {
          this.turnOffAssignParcel(false);
        }
      }
    },
    currentLayerId: {
      immediate: true,
      handler(nV, oV) {
        if (!this.currentLayer.tree_view) {
          this.treeViewMainLayerId = null;
          return;
        }
        if ((oV || oV === 0) && (nV || nV === 0) && nV === oV) {
          this.treeViewMainLayerId = null;
          return;
        }
        this.getTreeLayerMetadata(nV);
      },
    },
  },
  mounted() {
    this.$root.$on('turnOnCopying', this.turnOnCopying);
    this.$root.$off('addedSidebarGeometry');
    this.$root.$on('addedSidebarGeometry', this.onNewPolygonFeatureGeometry);
    this.$once('hook:beforeDestroy', () => {
      this.$root.$off('turnOnCopying');
    });
  },
};
</script>

<style lang="scss" scoped></style>
